import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthResponse as ApiAuthResponse } from '../utils/apiHelpers';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  name?: string;
  role: string;
  profile_pic: string | null;
  phone?: string;
  address?: string;
  [key: string]: unknown;
}

export function getUserDisplayName(user: AuthUser | null): string | null {
  if (!user) return null;
  const name = typeof user.name === 'string' ? user.name.trim() : '';
  if (name) return name;
  const username = typeof user.username === 'string' ? user.username.trim() : '';
  if (username) return username;
  const email = typeof user.email === 'string' ? user.email.trim() : '';
  return email || null;
}

export function authResponseToState(
  response: ApiAuthResponse | { user: AuthUser; token: string; refresh: string }
) {
  return {
    user: (response as any).user as AuthUser,
    token: (response as any).token as string,
    refreshToken: (response as any).refresh as string,
  };
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  hydrated: boolean;
  setAuth: (payload: {
    user: AuthUser;
    token: string;
    refreshToken?: string | null;
  }) => void;
  updateTokens: (token: string, refreshToken?: string | null) => void;
  logout: () => void;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      hydrated: false,
      setAuth: (payload) => set({
        user: payload.user,
        token: payload.token,
        refreshToken: payload.refreshToken ?? null,
      }),
      updateTokens: (token, refreshToken) => set((state) => ({
        token,
        refreshToken: refreshToken ?? state.refreshToken,
      })),
      logout: () => set({
        user: null,
        token: null,
        refreshToken: null,
      }),
      hydrate: () => set({ hydrated: true }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrate();
      },
    }
  )
);
